/* eslint-disable */
import { useTranslation } from "react-i18next";

export function formatThresholds(kpi) {
  const { t } = useTranslation();

  if (!kpi?.thresholdsConfig) {
    return t("thresholds.no_config");
  }

  const cfg = kpi.thresholdsConfig;
  const trackingMode = kpi.trackingMode; // Assumindo que kpi.trackingMode está disponível

  // Constantes para identificar "∞" e "-∞" usados no backend
  // Estes são os valores que o backend usa para representar faixas abertas
  // Se forem diferentes, ajuste aqui.
  const MIN_INF_BACKEND = -9999999999; // Representa -Infinito vindo do backend
  const MAX_INF_BACKEND = 9999999999; // Representa +Infinito vindo do backend

  /**
   * Converte min/max de fração (ex.: 0.8) para porcentagem
   * e decide se exibe "Acima de X%", "Abaixo de Y%" ou "Entre X% e Y%".
   *
   * @param {string} rangeKey - Chave de tradução para a cor/tipo da faixa (ex: "thresholds.green").
   * @param {number|null} min - Limite inferior da faixa (decimal, ex: 0.8).
   * @param {number|null} max - Limite superior da faixa (decimal, ex: 1.0).
   * @param {boolean} isModularRange - Indica se esta faixa faz parte de um conjunto modular (para "OU").
   */
  function formatIndividualRange(rangeKey, min, max, isModularRange = false) {
    // Multiplica por 100 para exibir como percentual, tratando nulls
    const minPct =
      min !== null && min !== undefined ? (min * 100).toFixed(0) : null;
    const maxPct =
      max !== null && max !== undefined ? (max * 100).toFixed(0) : null;

    // Define o label com a cor adequada
    let labelColorStyle = "";
    if (rangeKey.includes("green")) labelColorStyle = "color: green;";
    else if (rangeKey.includes("yellow")) labelColorStyle = "color: orange;";
    else if (rangeKey.includes("red")) labelColorStyle = "color: red;";

    const translatedLabel = `<span style="${labelColorStyle}">${t(rangeKey)}</span>`;
    const orSeparator = isModularRange ? ` ${t("thresholds.or")} ` : ""; // Adiciona "OU" para faixas modulares

    // Trata valores infinitos que vêm do backend
    const isMinInfinite = min === null || min <= MIN_INF_BACKEND;
    const isMaxInfinite = max === null || max >= MAX_INF_BACKEND;

    if (isMinInfinite && !isMaxInfinite) {
      // Abaixo de Y%
      return `${orSeparator}${translatedLabel} ${t("thresholds.below")} ${maxPct}%`;
    } else if (!isMinInfinite && isMaxInfinite) {
      // Acima de X%
      return `${orSeparator}${translatedLabel} ${t("thresholds.above")} ${minPct}%`;
    } else if (!isMinInfinite && !isMaxInfinite) {
      // Entre X% e Y%
      return `${orSeparator}${translatedLabel} ${t("thresholds.between")} ${minPct}% - ${maxPct}%`;
    } else {
      // Ambos infinitos, ou ambos null - não deveria acontecer para uma faixa válida
      return `${orSeparator}${translatedLabel}: ${t("thresholds.any_value")}`;
    }
  }

  const lines = [];
  lines.push(`<b>${t("thresholds.explanation_title")}</b>`); // Adiciona um título à explicação

  // GREEN: Sempre greenRange, independente do modo
  if (
    cfg.greenRange &&
    (cfg.greenRange[0] !== null || cfg.greenRange[1] !== null)
  ) {
    lines.push(
      "• " +
        formatIndividualRange(
          "thresholds.green",
          cfg.greenRange[0],
          cfg.greenRange[1]
        ).trim()
    );
  }

  // YELLOW: Diferente para MOD
  if (trackingMode === "MOD") {
    if (cfg.yellowRanges && cfg.yellowRanges.length > 0) {
      const yellowModularTexts = cfg.yellowRanges.map((range, index) =>
        formatIndividualRange(
          `thresholds.yellow_range`, // Usar uma chave genérica para "Faixa Amarela"
          range[0],
          range[1],
          index > 0 // Adiciona "OU" a partir da segunda faixa
        )
      );
      // Remove o "OU" inicial da primeira faixa se houver
      if (
        yellowModularTexts.length > 0 &&
        yellowModularTexts[0].startsWith(` ${t("thresholds.or")} `)
      ) {
        yellowModularTexts[0] = yellowModularTexts[0].substring(
          ` ${t("thresholds.or")} `.length
        );
      }
      lines.push("• " + yellowModularTexts.join("")); // Junta com "OU"
    }
  } else {
    // ASC ou DESC
    if (
      cfg.yellowRange &&
      (cfg.yellowRange[0] !== null || cfg.yellowRange[1] !== null)
    ) {
      lines.push(
        "• " +
          formatIndividualRange(
            "thresholds.yellow",
            cfg.yellowRange[0],
            cfg.yellowRange[1]
          ).trim()
      );
    }
  }

  // RED: Diferente para MOD
  if (trackingMode === "MOD") {
    if (cfg.redRanges && cfg.redRanges.length > 0) {
      const redModularTexts = cfg.redRanges.map((range, index) =>
        formatIndividualRange(
          `thresholds.red_range`, // Usar uma chave genérica para "Faixa Vermelha"
          range[0],
          range[1],
          index > 0 // Adiciona "OU" a partir da segunda faixa
        )
      );
      // Remove o "OU" inicial da primeira faixa se houver
      if (
        redModularTexts.length > 0 &&
        redModularTexts[0].startsWith(` ${t("thresholds.or")} `)
      ) {
        redModularTexts[0] = redModularTexts[0].substring(
          ` ${t("thresholds.or")} `.length
        );
      }
      lines.push("• " + redModularTexts.join("")); // Junta com "OU"
    }
  } else {
    // ASC ou DESC
    if (
      cfg.redRange &&
      (cfg.redRange[0] !== null || cfg.redRange[1] !== null)
    ) {
      lines.push(
        "• " +
          formatIndividualRange(
            "thresholds.red",
            cfg.redRange[0],
            cfg.redRange[1]
          ).trim()
      );
    }
  }

  // Se nenhuma faixa foi definida, informa o usuário
  if (lines.length === 1) {
    // Apenas o título foi adicionado
    lines.push(t("thresholds.no_ranges_defined"));
  }

  return lines.join("<br/>");
}
export function getProgressColor(ratio, trackingMode, thresholds) {
  // Adicionado export
  // Helper function to scale a single threshold value (e.g., 0.95 to 95)
  // Handles null or undefined values gracefully.
  const scaleVal = (v) => (v === null || v === undefined ? null : v * 100);

  // Scale all threshold values from the config to percent (0-100)
  const scaledThresholds = {
    greenRange: thresholds.greenRange
      ? [scaleVal(thresholds.greenRange[0]), scaleVal(thresholds.greenRange[1])]
      : null,
    yellowRange: thresholds.yellowRange // This is for ASC/DESC, MOD uses yellowRanges
      ? [
          scaleVal(thresholds.yellowRange[0]),
          scaleVal(thresholds.yellowRange[1]),
        ]
      : null,
    redRange: thresholds.redRange // This is for ASC/DESC, MOD uses redRanges
      ? [scaleVal(thresholds.redRange[0]), scaleVal(thresholds.redRange[1])]
      : null,
    // For MODULAR, scale each range within the arrays
    yellowRanges: thresholds.yellowRanges
      ? thresholds.yellowRanges.map((range) => [
          scaleVal(range[0]),
          scaleVal(range[1]),
        ])
      : null,
    redRanges: thresholds.redRanges
      ? thresholds.redRanges.map((range) => [
          scaleVal(range[0]),
          scaleVal(range[1]),
        ])
      : null,
  };

  const inRange = (val, rangeArray) => {
    if (!rangeArray) return false;
    const [rmin, rmax] = rangeArray;
    if (rmin === null && rmax === null) return false;
    if (rmin === null) return val <= rmax;
    if (rmax === null) return val >= rmin;
    return val >= rmin && val <= rmax;
  };

  const inAnyRange = (val, rangesList) => {
    if (!rangesList || !Array.isArray(rangesList)) return false;
    return rangesList.some((individualRange) => inRange(val, individualRange));
  };

  const ratioPercent = ratio * 100;
  let color = "#f44336"; // red default

  if (trackingMode === "MOD") {
    if (
      scaledThresholds.greenRange &&
      inRange(ratioPercent, scaledThresholds.greenRange)
    ) {
      color = "#4caf50";
    } else if (
      scaledThresholds.yellowRanges &&
      inAnyRange(ratioPercent, scaledThresholds.yellowRanges)
    ) {
      color = "#ffeb3b";
    } else if (
      scaledThresholds.redRanges &&
      inAnyRange(ratioPercent, scaledThresholds.redRanges)
    ) {
      color = "#f44336";
    }
    return color;
  }

  if (
    scaledThresholds.greenRange &&
    inRange(ratioPercent, scaledThresholds.greenRange)
  ) {
    color = "#4caf50";
  } else if (
    scaledThresholds.yellowRange &&
    inRange(ratioPercent, scaledThresholds.yellowRange)
  ) {
    color = "#ffeb3b";
  } else if (
    scaledThresholds.redRange &&
    inRange(ratioPercent, scaledThresholds.redRange)
  ) {
    color = "#f44336";
  }
  return color;
}
